import { useEffect, useState } from "react";
import AppBaseScreen from "../../components/AppBaseScreen";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { theme } from "./theme";
import {
    Box,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    Link,
    OutlinedInput,
    Radio,
    RadioGroup,
    ThemeProvider,
    Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { EMAIL_REGEX, MESSAGE_BAR_TYPES } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import AppButton from "../../components/AppButton";
import config from "../../utils/config";
import * as Yup from "yup";
import { styles } from "../Login/theme";
import Logo from "../../assets/logo/rocketfuel-circular.svg";
import { showMessageBar } from "../../store/actions";
import { useDispatch } from "react-redux";
import { getEmailOfreferal, referSignup } from "../../store/api";
import { executeRecaptcha } from "../../utils/recaptcha";
import AppScreenLoader from "../../components/AppScreenLoader";


const validationSchema = Yup.object({
    email: Yup.string()
        .matches(EMAIL_REGEX, "Invalid email")
        .required("Email is required"),

    accountType: Yup.string()
        .oneOf(["individual", "business"], "Invalid selection")
        .required("Please select an option"),

    firstName: Yup.string().when("accountType", {
        is: "individual",
        then: (schema) => schema.required("First Name is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    lastName: Yup.string().when("accountType", {
        is: "individual",
        then: (schema) => schema.required("Last Name is required"),
        otherwise: (schema) => schema.notRequired(),
    }),

    businessName: Yup.string().when("accountType", {
        is: "business",
        then: (schema) => schema.required("Business Name is required"),
        otherwise: (schema) => schema.notRequired(),
    }),
});


export default function ReferalSignup(props) {
    const [payeeData, setPayeeData] = useState(null);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const [step, setStep] = useState(0);
    const [searchParams] = useSearchParams();
    const [initialValues, setInititalValues] = useState({
        email: "",
        firstName: "",
        lastName: "",
        businessName: "",
        accountType: "individual", // default selection
    });
    const fetchPayeeData = async () => {
        const token = searchParams.get('token');
        if (token) {
            const response = await getEmailOfreferal(token)
            setPayeeData(response.result);
            setInititalValues({ ...initialValues, ...response.result })
        }
    }
    useEffect(() => {
        fetchPayeeData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleSignUp = async (values, { resetForm, setSubmitting }) => {
        try {
            const payload = {
                referCode: params?.referalId,
                ...Object.fromEntries(
                    Object.entries(values).filter(([_, v]) => v !== ""))
            }
            const invite = searchParams.get('token');
            if (invite) {
                payload.inviteCode = invite;
            }
            payload.recaptcha = await executeRecaptcha();

            const response = await referSignup(payload);
            if (response?.ok) {
                dispatch(showMessageBar(MESSAGE_BAR_TYPES.SUCCESS, t('invite.signup.complete')));
                navigate('/login', { state: { email: payload.email } })
            } else {
                dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, 'Error while sign up!'));
            }
            setSubmitting(false);
        } catch (err) {
            setSubmitting(false);
            dispatch(showMessageBar(MESSAGE_BAR_TYPES.ERROR, err));
        }
    };
    const { t } = useTranslation();
    if (searchParams.get('token') && !payeeData) {
        return <AppScreenLoader />
    }

    const SignUpComponent = () => {
        return <Box>
            <Typography fontSize={14} textAlign="center">
                {t("referpage.subtitle")}
            </Typography>
            <Box paddingTop={3} />
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSignUp}
                validateOnBlur={true}
                validateOnChange={false}
            >
                {({ values, errors, touched, handleChange, isSubmitting }) => (
                    <Form autoComplete="off">
                        <Box sx={{ display: "block" }}>
                            {/* Registering As (Radio Field) */}
                            <InputLabel>{t('invite.signup.register')}</InputLabel>
                            <Field name="accountType">
                                {({ field }) => (
                                    <FormControl
                                        component="fieldset"
                                        error={Boolean(errors.accountType && touched.accountType)}
                                    >
                                        <RadioGroup row {...field} onChange={handleChange}>
                                            <FormControlLabel
                                                value="individual"
                                                control={<Radio sx={{
                                                    color: '#f8f8f8',
                                                }} />}
                                                label="Individual"
                                            />
                                            <FormControlLabel
                                                value="business"
                                                control={<Radio sx={{
                                                    color: '#f8f8f8',
                                                }} />}
                                                label="Business"
                                            />
                                        </RadioGroup>
                                        {errors.accountType && touched.accountType && (
                                            <FormHelperText sx={{ marginLeft: 0 }}>{errors.accountType}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            </Field>
                            {values?.accountType === 'individual' && <Box>
                                <InputLabel>{t('invite.signup.firstName')}</InputLabel>
                                <FormHelperText>
                                    ({t('invite.signup.firstName.caption')})
                                </FormHelperText>
                                <Field name="firstName">
                                    {({ field }) => (
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            sx={{ maxWidth: 400 }}
                                            required
                                            error={Boolean(errors.firstName && touched.firstName)}
                                        >
                                            <OutlinedInput
                                                {...field}
                                                placeholder="John"
                                                autoComplete="off"
                                                size="small"
                                            />
                                            {errors.firstName && touched.firstName && (
                                                <FormHelperText sx={{ marginLeft: 0 }}>{errors.firstName}</FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                </Field>
                            </Box>}

                            {values?.accountType === 'individual' && <Box marginTop={2}>
                                <InputLabel>{t('invite.signup.lastName')}</InputLabel>
                                <FormHelperText>
                                    ({t('invite.signup.lastName.caption')})
                                </FormHelperText>
                                <Field name="lastName">
                                    {({ field }) => (
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            sx={{ maxWidth: 400 }}
                                            required
                                            error={Boolean(errors.lastName && touched.lastName)}
                                        >
                                            <OutlinedInput
                                                {...field}
                                                placeholder="Doe"
                                                autoComplete="off"
                                                size="small"
                                            />
                                            {errors.lastName && touched.lastName && (
                                                <FormHelperText sx={{ marginLeft: 0 }}>{errors.lastName}</FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                </Field>
                            </Box>}

                            {values?.accountType !== 'individual' && <Box>
                                <InputLabel>{t('invite.signup.businessName')}</InputLabel>
                                <FormHelperText>
                                    ({t('invite.signup.businessName.caption')})
                                </FormHelperText>
                                <Field name="businessName">
                                    {({ field }) => (
                                        <FormControl
                                            fullWidth
                                            variant="outlined"
                                            sx={{ maxWidth: 400 }}
                                            required
                                            error={Boolean(errors.lastName && touched.lastName)}
                                        >
                                            <OutlinedInput
                                                {...field}
                                                placeholder="ABC Company"
                                                autoComplete="off"
                                                size="small"
                                            />
                                            {errors.lastName && touched.lastName && (
                                                <FormHelperText sx={{ marginLeft: 0 }}>{errors.lastName}</FormHelperText>
                                            )}
                                        </FormControl>
                                    )}
                                </Field>
                            </Box>}

                            <Box marginTop={2} />
                            {/* Email */}
                            <InputLabel>{t('invite.signup.email')}</InputLabel>
                            <FormHelperText>
                                ({t('invite.signup.email.caption')})
                            </FormHelperText>
                            <Field name="email">
                                {({ field }) => (
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        sx={{ maxWidth: 400 }}
                                        required
                                        error={Boolean(errors.email && touched.email)}
                                    >
                                        <OutlinedInput
                                            {...field}
                                            placeholder={t("invite.text.placeholder")}
                                            autoComplete="off"
                                            size="small"
                                        />
                                        {errors.email && touched.email && (
                                            <FormHelperText sx={{ marginLeft: 0 }}>{errors.email}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            </Field>

                            <Box marginTop={3} />
                            <AppButton
                                loading={isSubmitting}
                                disabled={!values.email}
                                type="submit"
                                buttontheme="light"
                                title={t("loginPage.continue")}
                            />
                        </Box>
                    </Form>
                )}
            </Formik>
        </Box>;
    }

    const WelcomeComponent = () => {
        return <Box>
            <Card sx={{ borderRadius: 4, boxShadow: 3, padding: 1, marginTop: 2 }} >
                <CardContent>
                    <Typography variant="body1" paragraph>
                        {t("invite.description")}
                    </Typography>
                    <Typography variant="body1" paragraph>
                        {t("invite.description2")}
                    </Typography>
                    <Box my={2}>
                        <Typography variant="body1">{t("invite.feature_1")}</Typography>
                        <Typography variant="body1">{t("invite.feature_2")}</Typography>
                        <Typography variant="body1">{t("invite.feature_3")}</Typography>
                    </Box>
                    <Typography variant="body1" paragraph>
                        {t("invite.call_to_action")}
                    </Typography>

                    <AppButton
                        buttontheme="light"
                        onClick={() => setStep(1)}
                        title={t("loginPage.continue")}
                    />
                </CardContent>
            </Card>
        </Box>
    }
    return (
        <AppBaseScreen isHeader={false} childsize={"xs"} showLocale={true}>
            <ThemeProvider theme={theme}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: 2,
                    }}
                >
                    <Logo style={styles.logo} />
                </Box>
                <Typography fontSize={20} textAlign="center">
                    {t("loginPage.title")}
                </Typography>
                {step === 0 && <WelcomeComponent />}
                {step === 1 && <SignUpComponent />}
            </ThemeProvider>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: "center",
                    marginTop: 5,
                }}
            >
                <Link sx={{ marginTop: 2, marginBottom: 2 }} href={config.ABOUT_LINK} target="_blank">
                    {t("aboutUs")}
                </Link>
            </Box>
        </AppBaseScreen>
    );
}
